import FilledButtonM from "@common/components/button/FilledButtonM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { formatActivationDate } from "@common/utils/activationSuiteDisplay"
import {
    SUMMER_COURSES_OFFER_TYPE,
    SUMMER_MONTH_CHOICE,
    SUMMER_PRICES,
    SUMMER_RESUME_COMMITMENT,
    SUMMER_RESUME_TYPE,
    SUMMER_YEARLY_COMMITMENT_DISCOUNT,
    computeSummerPauseCommitmentShiftForMonth,
    getSummerCoursesOfferTypeLabel,
    getSummerCoursesPriceDescriptor,
    getSummerCoursesTimeslotsLabel,
    getSummerMonthChoiceValue,
    getSummerMonthCoursesConfig,
    getSummerMonthDateRange,
    getSummerMonthLabel,
    getSummerResumeTypeLabel,
    meetsRenewalDiscountFrequency,
} from "@common/utils/summer2026"
import React, { useMemo } from "react"
import { StyleSheet, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"

const COMMITMENT_HALF = SUMMER_YEARLY_COMMITMENT_DISCOUNT / 2

const Chip = ({ icon, label, tone = "neutral" }) => {
    const palette =
        tone === "primary"
            ? {
                  bg:
                      theme.colors.primaryLighter ||
                      theme.colors.lightMoreTransparent,
                  fg: theme.colors.primary,
              }
            : tone === "success"
              ? { bg: "#E6F4EA", fg: "#1B7A3D" }
              : tone === "warning"
                ? {
                      bg: theme.colors.summerLight,
                      fg: theme.colors.summerDeep,
                  }
                : {
                      bg: theme.colors.lightMore,
                      fg: theme.colors.grey,
                  }
    return (
        <View style={[styles.chip, { backgroundColor: palette.bg }]}>
            {icon ? (
                <FontAwesome5
                    name={icon}
                    solid
                    size={10}
                    color={palette.fg}
                    style={{ marginRight: 4 }}
                />
            ) : null}
            <TextM fontWeight={"Bold"} fontSize={11} color={palette.fg}>
                {label}
            </TextM>
        </View>
    )
}

// Badge ribbon-style en haut-droite, utilisé pour mettre en avant
// -50% / 100€ offerts sans envahir la card.
const HighlightRibbon = ({ label, color = theme.colors.error }) => (
    <View style={[styles.ribbon, { backgroundColor: color }]}>
        <FontAwesome5
            name={"bolt"}
            solid
            size={9}
            color={theme.colors.white}
            style={{ marginRight: 4 }}
        />
        <TextM
            fontWeight={"ExtraBold"}
            fontSize={10}
            color={theme.colors.white}
        >
            {label}
        </TextM>
    </View>
)

// Visual variant table — drives the card background, border, header
// accent color, and "decided" state. Centralized so each variant
// shares a coherent palette pulled from the theme. Undecided variant
// uses a dashed border + neutral background to clearly read "action
// needed" vs. confirmed variants that use a tinted fill matching the
// choice (warm orange for Cours d'été, primary for Autonomy/Rentrée,
// neutral grey for Pause).
const VARIANT_STYLES = {
    undecided: {
        bg: theme.colors.white,
        border: theme.colors.lightMore,
        borderStyle: "dashed",
        borderWidth: 1.5,
        headerColor: theme.colors.grey,
        decided: false,
    },
    summer: {
        bg: theme.colors.summerLight,
        border: theme.colors.summer,
        borderStyle: "solid",
        borderWidth: 2,
        headerColor: theme.colors.summerDeep,
        decided: true,
    },
    primary: {
        bg: theme.colors.primaryLighter,
        border: theme.colors.primary,
        borderStyle: "solid",
        borderWidth: 2,
        headerColor: theme.colors.primary,
        decided: true,
    },
    pause: {
        bg: theme.colors.greyBg,
        border: theme.colors.light,
        borderStyle: "solid",
        borderWidth: 2,
        headerColor: theme.colors.grey,
        decided: true,
    },
    // Variant for the Rentrée card — primary-tinted whether or not a
    // choice is set (it's not a month, so the undecided state should
    // still feel like a real card, just slightly muted).
    rentree: {
        bg: theme.colors.white,
        border: theme.colors.primaryLighter || theme.colors.lightMore,
        borderStyle: "solid",
        borderWidth: 1.5,
        headerColor: theme.colors.primary,
        decided: false,
    },
}

// Small confirmed badge — green checkmark pill, anchored top-left.
// Materializes "you've made a choice" at a glance, even before reading
// the title. Doesn't conflict with the ribbon (top-right).
const ConfirmedBadge = () => (
    <View style={styles.confirmedBadge}>
        <FontAwesome5
            name={"check"}
            solid
            size={9}
            color={theme.colors.white}
            style={{ marginRight: 4 }}
        />
        <TextM
            fontWeight={"ExtraBold"}
            fontSize={10}
            color={theme.colors.white}
        >
            Choisi
        </TextM>
    </View>
)

const CardShell = ({
    header,
    headerIcon,
    title,
    subtitle,
    children,
    onEdit,
    style,
    variant = "undecided",
    ribbon,
    locked = false,
    lockedLabel,
    hideEditButton = false,
}) => {
    const v = VARIANT_STYLES[variant] || VARIANT_STYLES.undecided
    return (
        <View
            style={[
                styles.card,
                {
                    backgroundColor: v.bg,
                    borderColor: v.border,
                    borderStyle: v.borderStyle,
                    borderWidth: v.borderWidth,
                },
                style,
            ]}
        >
            {v.decided ? <ConfirmedBadge /> : null}
            {ribbon ? <View style={styles.ribbonAnchor}>{ribbon}</View> : null}
            <View style={styles.headerRow}>
                <View style={{ flex: 1 }}>
                    <View style={styles.headerLabelRow}>
                        {headerIcon ? (
                            <FontAwesome5
                                name={headerIcon}
                                solid
                                size={12}
                                color={v.headerColor}
                                style={{ marginRight: 6 }}
                            />
                        ) : null}
                        <TextM
                            fontWeight={"Bold"}
                            fontSize={12}
                            color={v.headerColor}
                            uppercase
                            style={{ letterSpacing: 0.4 }}
                        >
                            {header}
                        </TextM>
                    </View>
                    <TextM
                        fontWeight={"Bold"}
                        fontSize={18}
                        style={{ marginTop: 4 }}
                        color={
                            v.decided ? theme.colors.text : theme.colors.grey
                        }
                        wrap
                    >
                        {title}
                    </TextM>
                    {subtitle ? (
                        <TextM
                            fontSize={12}
                            color={theme.colors.grey}
                            wrap
                            style={{ marginTop: 2 }}
                        >
                            {subtitle}
                        </TextM>
                    ) : null}
                </View>
                {hideEditButton ? null : (
                    <FilledButtonM
                        icon={
                            locked
                                ? "lock"
                                : v.decided
                                  ? "pencil"
                                  : "arrow-right"
                        }
                        label={
                            locked
                                ? lockedLabel || "Verrouillé"
                                : v.decided
                                  ? "Modifier"
                                  : "Choisir"
                        }
                        mode={v.decided || locked ? "outlined" : "contained"}
                        color={
                            locked || v.decided
                                ? undefined
                                : theme.colors.primary
                        }
                        style={{
                            width: null,
                            paddingHorizontal: 14,
                            marginBottom: 0,
                            opacity: locked ? 0.7 : 1,
                        }}
                        contentStyle={{ height: 32, paddingHorizontal: 0 }}
                        labelStyle={{ fontSize: 11, marginVertical: 0 }}
                        onPress={onEdit}
                    />
                )}
            </View>
            <View style={{ marginTop: 14 }}>{children}</View>
        </View>
    )
}

// Line affichée sous le récap d'un mois pausé pour rappeler que
// l'engagement courant est décalé de la durée de la pause (mirroir UX de
// `ParentPauseSubscriptionModal` étape 2). Lit `user.commitment_ends_at`
// — si pas d'engagement futur, on ne rend rien (pas d'info à donner).
// Pour la pause manuelle qui couvre déjà ce mois (`forcedReadOnlyReason`),
// le décalage est déjà capturé par la pause existante côté backend : on
// laisse la carte read-only sans dupliquer le message ici.
const PauseCommitmentShiftLine = ({ user, month }) => {
    const shift = computeSummerPauseCommitmentShiftForMonth(user, month)
    if (!shift.hasCommitment || !shift.days) return null
    return (
        <View style={{ marginTop: 6 }}>
            <TextM
                fontSize={12}
                color={theme.colors.grey}
                wrap
                style={{ lineHeight: 17 }}
            >
                {"Engagement décalé de "}
                <TextM
                    fontWeight={"Bold"}
                    fontSize={12}
                    color={theme.colors.text}
                >
                    {`${shift.days} jours`}
                </TextM>
                {", il se terminera le "}
                <TextM
                    fontWeight={"Bold"}
                    fontSize={12}
                    color={theme.colors.text}
                >
                    {shift.newCommitment.format("DD/MM/YYYY")}
                </TextM>
                {" (au lieu du "}
                {shift.oldCommitment.format("DD/MM/YYYY")}
                {")."}
            </TextM>
        </View>
    )
}

// Map the month choice to the matching card variant. Drives the
// whole-card visual state (background, border, decided badge) so each
// option reads at a glance — Cours d'été = warm orange, Autonomy =
// primary, Pause = muted grey, no choice = dashed neutral.
const choiceToVariant = (choice) => {
    if (choice === SUMMER_MONTH_CHOICE.SUMMER_COURSES) return "summer"
    if (choice === SUMMER_MONTH_CHOICE.AUTONOMY) return "primary"
    if (choice === SUMMER_MONTH_CHOICE.PAUSE) return "pause"
    return "undecided"
}

const choiceIcon = (choice) => {
    if (choice === SUMMER_MONTH_CHOICE.SUMMER_COURSES) return "sun"
    if (choice === SUMMER_MONTH_CHOICE.AUTONOMY) return "user-graduate"
    if (choice === SUMMER_MONTH_CHOICE.PAUSE) return "pause-circle"
    return "question-circle"
}

const choiceLabel = (choice) => {
    if (choice === SUMMER_MONTH_CHOICE.SUMMER_COURSES) return "Cours d'été"
    if (choice === SUMMER_MONTH_CHOICE.AUTONOMY) return "Mode Autonome"
    if (choice === SUMMER_MONTH_CHOICE.PAUSE) return "Pause"
    return "À choisir"
}

const getCoursesDetailLabel = (config) => {
    if (!config) return null
    if (config.offerType === SUMMER_COURSES_OFFER_TYPE.SHARED) {
        const tsLabel = getSummerCoursesTimeslotsLabel(config.slots_possible)
        return tsLabel || null
    }
    const hours = config.hoursPerWeek
    return hours ? `${hours}h/sem · Cours particuliers` : null
}

// =====================================================================
// SummerMonthPlanCard — récap pour un mois (juillet ou août). Accent
// "summer" (border/header en orange chaud) + icône sun pour bien
// distinguer des cards de rentrée. Ribbon -50% si pack 3 créneaux.
// =====================================================================
export const SummerMonthPlanCard = ({
    month,
    slot,
    onEdit,
    schoolSubjects,
    user = null,
    summerConfig = null,
    locked = false,
    forcedReadOnlyReason = null,
}) => {
    const choice = getSummerMonthChoiceValue(slot)
    const coursesConfig = getSummerMonthCoursesConfig(slot)
    const priceDescriptor =
        choice === SUMMER_MONTH_CHOICE.SUMMER_COURSES
            ? getSummerCoursesPriceDescriptor(coursesConfig, summerConfig)
            : null

    const subjectsLabel =
        coursesConfig?.subjectIds?.length && schoolSubjects?.length
            ? schoolSubjects
                  .filter((s) => coursesConfig.subjectIds.includes(s.id))
                  .map((s) => s.name)
                  .join(", ")
            : null

    const detailLabel = getCoursesDetailLabel(coursesConfig)
    const offerTypeLabel = coursesConfig
        ? getSummerCoursesOfferTypeLabel(coursesConfig.offerType)
        : null

    const showHeroRibbon = !!priceDescriptor?.isHeroOffer

    // Forced read-only override (e.g. pause manuelle déjà programmée qui
    // couvre ce mois). On affiche un état neutre, l'utilisateur ne peut
    // pas modifier — c'est la pause Stripe en place qui décide.
    if (forcedReadOnlyReason) {
        return (
            <CardShell
                header={getSummerMonthLabel(month)}
                headerIcon={"pause-circle"}
                title={"Pause programmée"}
                subtitle={forcedReadOnlyReason}
                variant={"pause"}
                hideEditButton
            >
                <View style={styles.detailBlock}>
                    <TextM fontSize={12} color={theme.colors.grey} wrap>
                        Ce mois est couvert par une pause déjà programmée sur
                        votre abonnement — aucun prélèvement.
                    </TextM>
                </View>
            </CardShell>
        )
    }

    const variant = choiceToVariant(choice)
    // Header icon adapts to the choice so the month label badge in the
    // top-left already telegraphs the state (sun/grad/pause/?) — even
    // before the parent reads the title.
    const headerIcon = choice ? choiceIcon(choice) : "question-circle"

    return (
        <CardShell
            header={getSummerMonthLabel(month)}
            headerIcon={headerIcon}
            title={choiceLabel(choice)}
            subtitle={getSummerMonthDateRange(month)}
            onEdit={onEdit}
            variant={variant}
            locked={locked}
            lockedLabel={"En cours"}
            ribbon={
                showHeroRibbon ? (
                    <HighlightRibbon label={"−50% pack 3 créneaux"} />
                ) : null
            }
        >
            {!choice ? (
                <Chip icon={"question-circle"} label={"Aucun choix posé"} />
            ) : null}

            {choice === SUMMER_MONTH_CHOICE.SUMMER_COURSES &&
            coursesConfig &&
            priceDescriptor ? (
                <View style={styles.detailBlock}>
                    <View style={styles.detailRow}>
                        <FontAwesome5
                            name={
                                coursesConfig.offerType ===
                                SUMMER_COURSES_OFFER_TYPE.SHARED
                                    ? "calendar-alt"
                                    : "clock"
                            }
                            size={11}
                            color={theme.colors.grey}
                            style={{ width: 16 }}
                        />
                        <TextM
                            fontSize={13}
                            color={theme.colors.text}
                            style={{ flex: 1, marginLeft: 6 }}
                            wrap
                        >
                            {detailLabel || offerTypeLabel}
                        </TextM>
                    </View>
                    {coursesConfig.offerType ===
                    SUMMER_COURSES_OFFER_TYPE.SHARED ? (
                        <View style={styles.detailRow}>
                            <FontAwesome5
                                name={"users"}
                                size={11}
                                color={theme.colors.grey}
                                style={{ width: 16 }}
                            />
                            <TextM
                                fontSize={13}
                                color={theme.colors.text}
                                style={{ flex: 1, marginLeft: 6 }}
                                wrap
                            >
                                {offerTypeLabel}
                            </TextM>
                        </View>
                    ) : null}
                    {subjectsLabel ? (
                        <View style={styles.detailRow}>
                            <FontAwesome5
                                name={"book"}
                                size={11}
                                color={theme.colors.grey}
                                style={{ width: 16 }}
                            />
                            <TextM
                                fontSize={13}
                                color={theme.colors.text}
                                style={{ flex: 1, marginLeft: 6 }}
                                wrap
                            >
                                {subjectsLabel}
                            </TextM>
                        </View>
                    ) : null}
                    <View style={styles.priceRow}>
                        {priceDescriptor.flexEquivalentPrice >
                        priceDescriptor.monthlyPrice ? (
                            <TextM
                                fontSize={13}
                                color={theme.colors.grey}
                                style={{
                                    textDecorationLine: "line-through",
                                    marginRight: 6,
                                }}
                            >
                                {priceDescriptor.flexEquivalentPrice}€
                            </TextM>
                        ) : null}
                        <TextM
                            fontWeight={"Bold"}
                            fontSize={22}
                            color={
                                showHeroRibbon
                                    ? theme.colors.summerDeep
                                    : theme.colors.primary
                            }
                        >
                            {priceDescriptor.monthlyPrice}€
                        </TextM>
                        <TextM
                            fontSize={12}
                            color={theme.colors.grey}
                            style={{ marginLeft: 4 }}
                        >
                            / mois
                        </TextM>
                    </View>
                    {priceDescriptor.savings > 0 ? (
                        <TextM
                            fontSize={11}
                            color={theme.colors.summerDeep}
                            fontWeight={"Bold"}
                            wrap
                        >
                            Économie {priceDescriptor.savings}€/mois vs abo
                            actuel
                        </TextM>
                    ) : null}
                </View>
            ) : null}

            {choice === SUMMER_MONTH_CHOICE.SUMMER_COURSES &&
            (!coursesConfig || !priceDescriptor) ? (
                <View style={styles.detailBlock}>
                    <TextM fontSize={12} color={theme.colors.grey} wrap>
                        Configurez les créneaux et matières pour ce mois.
                    </TextM>
                </View>
            ) : null}

            {choice === SUMMER_MONTH_CHOICE.AUTONOMY ? (
                <View style={styles.detailBlock}>
                    <View style={styles.priceRow}>
                        <TextM
                            fontWeight={"Bold"}
                            fontSize={20}
                            color={theme.colors.primary}
                        >
                            {SUMMER_PRICES.AUTONOMY_MONTHLY_PRICE}€
                        </TextM>
                        <TextM
                            fontSize={12}
                            color={theme.colors.grey}
                            style={{ marginLeft: 4 }}
                        >
                            / mois
                        </TextM>
                    </View>
                    <TextM
                        fontSize={12}
                        color={theme.colors.grey}
                        wrap
                        style={{ marginTop: 4 }}
                    >
                        Plateforme + IA + 1 cours tuteur offert
                    </TextM>
                </View>
            ) : null}

            {choice === SUMMER_MONTH_CHOICE.PAUSE ? (
                <View style={styles.detailBlock}>
                    <TextM
                        fontWeight={"Bold"}
                        fontSize={16}
                        color={theme.colors.grey}
                    >
                        Aucun prélèvement
                    </TextM>
                    <PauseCommitmentShiftLine user={user} month={month} />
                </View>
            ) : null}

            {!choice ? (
                <View style={styles.detailBlock}>
                    <TextM fontSize={12} color={theme.colors.grey} wrap>
                        Choisissez le mode pour ce mois : cours d&apos;été, mode
                        autonome ou pause.
                    </TextM>
                </View>
            ) : null}
        </CardShell>
    )
}

// =====================================================================
// SummerResumePlanCard — récap reprise septembre. Accent "primary"
// pour distinguer des deux cards estivales. Ribbon "🎁 100€" si
// engagement annuel sélectionné.
// =====================================================================
export const SummerResumePlanCard = ({
    resume,
    onEdit,
    pricingTable,
    sessionsPerWeek,
    schoolSubjects,
    renewalDiscountEligible = false,
}) => {
    const { type, frequency, commitment, resumeDate, subjectIds } = resume || {}

    // Title intentionally omits the commitment segment — engagement
    // status is already surfaced elsewhere in the portal, no need to
    // repeat it inside this card.
    const offerLabel = useMemo(() => {
        if (!type) return "À choisir"
        const typeLabel = getSummerResumeTypeLabel(type)
        if (type === SUMMER_RESUME_TYPE.AUTONOMY) return typeLabel
        const freq = frequency ? `${frequency}h / sem` : null
        return [typeLabel, freq].filter(Boolean).join(" · ")
    }, [type, frequency])

    const dateLabel = resumeDate
        ? `Reprise le ${formatActivationDate(resumeDate)}`
        : "Date à choisir"

    const subjectsLabel = useMemo(() => {
        if (!subjectIds?.length || !schoolSubjects?.length) return null
        return schoolSubjects
            .filter((s) => subjectIds.includes(s.id))
            .map((s) => s.name)
            .join(", ")
    }, [subjectIds, schoolSubjects])

    const pricing = useMemo(() => {
        if (
            !(
                (type === SUMMER_RESUME_TYPE.FLEX_SHARED ||
                    type === SUMMER_RESUME_TYPE.FLEX_INDIVIDUAL) &&
                frequency &&
                pricingTable &&
                sessionsPerWeek
            )
        ) {
            return null
        }
        const offerType =
            type === SUMMER_RESUME_TYPE.FLEX_INDIVIDUAL
                ? "integral"
                : "essential"
        const withCommitment =
            commitment === SUMMER_RESUME_COMMITMENT.YEARLY_12M
        try {
            const offerPricing = Object.keys(
                pricingTable?.[offerType] ?? {}
            ).reduce((acc, hoursPerMonth) => {
                const weeklyHours = sessionsPerWeek?.[hoursPerMonth]
                const hourlyPrice =
                    pricingTable[offerType][hoursPerMonth]?.[
                        withCommitment ? "commitment" : "base"
                    ]
                return {
                    ...acc,
                    [weeklyHours]: { weeklyHours, hoursPerMonth, hourlyPrice },
                }
            }, {})
            const row = offerPricing[frequency]
            if (row) {
                const monthlyPrice =
                    Math.round(row.hourlyPrice * row.hoursPerMonth * 10) / 10
                return {
                    monthlyPrice,
                    hourlyPrice: row.hourlyPrice,
                    hoursPerMonth: row.hoursPerMonth,
                }
            }
            return null
        } catch {
            return null
        }
    }, [type, frequency, commitment, pricingTable, sessionsPerWeek])

    // Le ruban "🎁 100€ offerts" et la ligne 50+50 ne s'appliquent qu'aux
    // parents qui se réengagent effectivement à la rentrée — i.e. engagement
    // projeté qui se termine AVANT la date de reprise (ou pas d'engagement),
    // ET à partir de 2h/sem (1h/sem exclue). Source de vérité partagée avec
    // le gift box du cockpit (`shouldOfferRenewalDiscount` +
    // `meetsRenewalDiscountFrequency`) et le mail de confirmation
    // (`SummerChoiceConfirmedNotification::isEngagementEligible`).
    const showYearlyRibbon =
        renewalDiscountEligible &&
        commitment === SUMMER_RESUME_COMMITMENT.YEARLY_12M &&
        meetsRenewalDiscountFrequency(frequency)

    // Rentrée: decided → primary (tinted background, confirmed badge),
    // undecided → rentree (neutral white but still primary-headed).
    const variant = type ? "primary" : "rentree"

    return (
        <CardShell
            header={"Rentrée"}
            headerIcon={"play-circle"}
            title={offerLabel}
            subtitle={dateLabel}
            onEdit={onEdit}
            variant={variant}
            ribbon={
                showYearlyRibbon ? (
                    <HighlightRibbon
                        label={`🎁 ${SUMMER_YEARLY_COMMITMENT_DISCOUNT}€ offerts`}
                        color={theme.colors.success}
                    />
                ) : null
            }
        >
            {!type ? (
                <Chip icon={"question-circle"} label={"Aucun choix posé"} />
            ) : null}

            <View style={styles.detailBlock}>
                {subjectsLabel ? (
                    <View style={styles.detailRow}>
                        <FontAwesome5
                            name={"book"}
                            size={11}
                            color={theme.colors.grey}
                            style={{ width: 16 }}
                        />
                        <TextM
                            fontSize={13}
                            color={theme.colors.text}
                            style={{ flex: 1, marginLeft: 6 }}
                            wrap
                        >
                            {subjectsLabel}
                        </TextM>
                    </View>
                ) : null}

                {showYearlyRibbon ? (
                    <View style={styles.detailRow}>
                        <FontAwesome5
                            name={"gift"}
                            size={11}
                            color={theme.colors.success}
                            style={{ width: 16 }}
                        />
                        <TextM
                            fontSize={13}
                            color={theme.colors.text}
                            style={{ flex: 1, marginLeft: 6 }}
                            wrap
                        >
                            {COMMITMENT_HALF}€ M1 + {COMMITMENT_HALF}€ M2
                        </TextM>
                    </View>
                ) : null}

                {pricing ? (
                    <View style={styles.priceRow}>
                        <TextM
                            fontWeight={"Bold"}
                            fontSize={22}
                            color={theme.colors.primary}
                        >
                            {pricing.monthlyPrice}€
                        </TextM>
                        <TextM
                            fontSize={12}
                            color={theme.colors.grey}
                            style={{ marginLeft: 4 }}
                        >
                            / mois
                        </TextM>
                    </View>
                ) : type === SUMMER_RESUME_TYPE.AUTONOMY ? (
                    <View style={styles.priceRow}>
                        <TextM
                            fontWeight={"Bold"}
                            fontSize={22}
                            color={theme.colors.primary}
                        >
                            {SUMMER_PRICES.AUTONOMY_MONTHLY_PRICE}€
                        </TextM>
                        <TextM
                            fontSize={12}
                            color={theme.colors.grey}
                            style={{ marginLeft: 4 }}
                        >
                            / mois
                        </TextM>
                    </View>
                ) : null}

                {!type ? (
                    <TextM fontSize={12} color={theme.colors.grey} wrap>
                        Choisissez l&apos;offre, les matières et la date à
                        laquelle votre abonnement reprend.
                    </TextM>
                ) : null}
            </View>
        </CardShell>
    )
}

const styles = StyleSheet.create({
    card: {
        padding: 18,
        borderRadius: 16,
        position: "relative",
        overflow: "visible",
    },
    confirmedBadge: {
        position: "absolute",
        top: -10,
        left: 14,
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: theme.colors.success,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 999,
        shadowColor: "#000",
        shadowOpacity: 0.12,
        shadowRadius: 3,
        shadowOffset: { width: 0, height: 2 },
        elevation: 3,
        zIndex: 2,
    },
    ribbonAnchor: {
        position: "absolute",
        top: -10,
        right: 14,
        zIndex: 2,
    },
    ribbon: {
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 999,
        shadowColor: "#000",
        shadowOpacity: 0.1,
        shadowRadius: 4,
        shadowOffset: { width: 0, height: 2 },
        elevation: 3,
    },
    headerRow: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 10,
    },
    headerLabelRow: {
        flexDirection: "row",
        alignItems: "center",
    },
    chip: {
        alignSelf: "flex-start",
        flexDirection: "row",
        alignItems: "center",
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 999,
    },
    detailBlock: {
        marginTop: 12,
        gap: 6,
    },
    detailRow: {
        flexDirection: "row",
        alignItems: "center",
    },
    priceRow: {
        flexDirection: "row",
        alignItems: "baseline",
        marginTop: 6,
    },
})
